import { useState, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import {
  MagnifyingGlassIcon,
  FunnelIcon,
  ArrowPathIcon,
  EyeIcon,
  EyeSlashIcon,
  MapPinIcon,
  AdjustmentsHorizontalIcon,
  XMarkIcon,
  ChevronUpIcon,
  SparklesIcon,
  ExclamationTriangleIcon,
  Cog6ToothIcon
} from '@heroicons/react/24/outline';
import VoterMapComponent from '../components/VoterMapComponent';
import MapLegend from '../components/MapLegend';
import VisualizationLegend from '../components/VisualizationLegend';
import NaturalLanguageBar from '../components/NaturalLanguageBar';
import RouteOptimizer from '../components/RouteOptimizer';
import RouteVisualization from '../components/RouteVisualization';
import CanvassingConfigPanel from '../components/CanvassingConfigPanel';
import FailedGeocodesPanel from '../components/FailedGeocodesPanel';
import { useNavigate } from 'react-router-dom';
import { getVoters, getDatasets, getDatasetStats, getDataset, generateVisualization, optimizeRoutes, createRoute } from '../services/api';

// Default color schemes
const defaultColorSchemes = {
  party: {
    R: '#DC2626',
    D: '#2563EB',
    I: '#7C3AED',
    L: '#F59E0B',
    G: '#10B981',
    S: '#14B8A6',
    SV: '#14B8A6',
    default: '#6B7280'
  },
  sex: {
    M: '#3B82F6',
    F: '#EC4899',
    default: '#6B7280'
  },
  age: {
    '18-25': '#10B981',
    '26-35': '#3B82F6',
    '36-50': '#F59E0B',
    '51-65': '#F97316',
    '65+': '#EF4444',
    default: '#6B7280'
  }
};

const defaultLabels = {
  party: {
    R: 'Republican',
    D: 'Democrat',
    I: 'Independent',
    L: 'Libertarian',
    G: 'Green',
    S: 'Swing',
    SV: 'Swing',
    default: 'Other'
  },
  sex: {
    M: 'Male',
    F: 'Female',
    default: 'Unknown'
  },
  age: {
    '18-25': '18-25 years',
    '26-35': '26-35 years',
    '36-50': '36-50 years',
    '51-65': '51-65 years',
    '65+': '65+ years',
    default: 'Unknown'
  }
};

function FilterPanel({ filters, onFilterChange, stats }) {
  return (
    <div className="bg-white rounded-lg shadow p-4 space-y-4">
      <h3 className="text-sm font-semibold text-gray-700">Filters</h3>

      {/* Party filter */}
      <div>
        <label className="text-xs font-medium text-gray-600">Party</label>
        <div className="mt-1 space-y-1">
          {['R', 'D', 'I', 'L', 'G', 'S'].map(party => (
            <label key={party} className="flex items-center">
              <input
                type="checkbox"
                checked={filters.party?.includes(party) ?? true}
                onChange={(e) => {
                  const current = filters.party || ['R', 'D', 'I', 'L', 'G'];
                  if (e.target.checked) {
                    onFilterChange('party', [...current, party]);
                  } else {
                    onFilterChange('party', current.filter(p => p !== party));
                  }
                }}
                className="h-4 w-4 text-blue-600 rounded border-gray-300"
              />
              <span className="ml-2 text-sm text-gray-700">
                {defaultLabels.party[party]}
                {stats?.party?.[party] && (
                  <span className="text-gray-400 ml-1">
                    ({stats.party[party].toLocaleString()})
                  </span>
                )}
              </span>
            </label>
          ))}
        </div>
      </div>

      {/* Sex filter */}
      <div>
        <label className="text-xs font-medium text-gray-600">Sex</label>
        <div className="mt-1 space-y-1">
          {['M', 'F'].map(sex => (
            <label key={sex} className="flex items-center">
              <input
                type="checkbox"
                checked={filters.sex?.includes(sex) ?? true}
                onChange={(e) => {
                  const current = filters.sex || ['M', 'F'];
                  if (e.target.checked) {
                    onFilterChange('sex', [...current, sex]);
                  } else {
                    onFilterChange('sex', current.filter(s => s !== sex));
                  }
                }}
                className="h-4 w-4 text-blue-600 rounded border-gray-300"
              />
              <span className="ml-2 text-sm text-gray-700">
                {defaultLabels.sex[sex]}
                {stats?.sex?.[sex] && (
                  <span className="text-gray-400 ml-1">
                    ({stats.sex[sex].toLocaleString()})
                  </span>
                )}
              </span>
            </label>
          ))}
        </div>
      </div>

      {/* Age range filter */}
      <div>
        <label className="text-xs font-medium text-gray-600">Age Range</label>
        <div className="mt-1 grid grid-cols-2 gap-2">
          <input
            type="number"
            placeholder="Min"
            value={filters.ageMin || ''}
            onChange={(e) => onFilterChange('ageMin', e.target.value ? parseInt(e.target.value) : null)}
            className="px-2 py-1 text-sm border border-gray-300 rounded"
          />
          <input
            type="number"
            placeholder="Max"
            value={filters.ageMax || ''}
            onChange={(e) => onFilterChange('ageMax', e.target.value ? parseInt(e.target.value) : null)}
            className="px-2 py-1 text-sm border border-gray-300 rounded"
          />
        </div>
      </div>

      {/* Previous votes filter */}
      <div>
        <label className="text-xs font-medium text-gray-600">Min Previous Votes</label>
        <input
          type="number"
          min="0"
          value={filters.minPreviousVotes || ''}
          onChange={(e) => onFilterChange('minPreviousVotes', e.target.value ? parseInt(e.target.value) : null)}
          className="mt-1 w-full px-2 py-1 text-sm border border-gray-300 rounded"
        />
      </div>

      {/* Reset filters */}
      <button
        onClick={() => onFilterChange('reset', null)}
        className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-50 rounded border border-gray-200"
      >
        <ArrowPathIcon className="h-4 w-4" />
        Reset Filters
      </button>
    </div>
  );
}

// Tab navigation
function TabNavigation({ activeTab, onTabChange }) {
  const tabs = [
    { id: 'visualize', label: 'Visualize', icon: EyeIcon },
    { id: 'routes', label: 'Routes', icon: MapPinIcon }
  ];

  return (
    <div className="flex border-b border-gray-200 mb-4">
      {tabs.map(tab => (
        <button
          key={tab.id}
          onClick={() => onTabChange(tab.id)}
          className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === tab.id
              ? 'border-blue-600 text-blue-600'
              : 'border-transparent text-gray-600 hover:text-gray-800 hover:border-gray-300'
          }`}
        >
          <tab.icon className="h-4 w-4" />
          {tab.label}
        </button>
      ))}
    </div>
  );
}

export default function VoterMap() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const datasetId = searchParams.get('dataset');

  // UI state
  const [activeTab, setActiveTab] = useState('visualize');
  const [showFilters, setShowFilters] = useState(false);
  const [showClusters, setShowClusters] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showMobileSidebar, setShowMobileSidebar] = useState(false);
  const [showMobilePromptBar, setShowMobilePromptBar] = useState(false);

  // Visualization state
  const [colorBy, setColorBy] = useState('party');
  const [colorScheme, setColorScheme] = useState(defaultColorSchemes.party);
  const [labels, setLabels] = useState(defaultLabels.party);
  const [visualizationSpec, setVisualizationSpec] = useState(null);
  const [highlightedVoterIds, setHighlightedVoterIds] = useState([]);
  const [clusterConfig, setClusterConfig] = useState({
    colorField: null,
    colorScheme: null,
    showComposition: true,
    sizeField: null,
    highlightRatio: true
  });

  // Heatmap layer configuration
  const [heatmapConfig, setHeatmapConfig] = useState({
    show: false,
    field: null,
    color: '#3B82F6',
    radius: 25,
    blur: 15,
    opacity: 0.6
  });

  // Bubble/proportional symbols configuration
  const [bubbleConfig, setBubbleConfig] = useState({
    show: false,
    sizeField: null,
    minRadius: 8,
    maxRadius: 40,
    opacity: 0.7
  });

  // Hexbin aggregation configuration
  const [hexbinConfig, setHexbinConfig] = useState({
    show: false,
    radius: 50,
    colorField: null,
    colorScheme: null,
    opacity: 0.7
  });

  // Route optimization state
  const [routes, setRoutes] = useState([]);
  const [showRoutes, setShowRoutes] = useState(false);
  const [selectedRoute, setSelectedRoute] = useState(null);
  const [showCanvassingConfig, setShowCanvassingConfig] = useState(false);

  // Filter state
  const [filters, setFilters] = useState({});

  // Fetch datasets
  const { data: datasetsData } = useQuery({
    queryKey: ['datasets'],
    queryFn: getDatasets
  });

  // Fetch current dataset details (for geocoding status)
  const { data: datasetData } = useQuery({
    queryKey: ['dataset', datasetId],
    queryFn: () => getDataset(datasetId),
    enabled: !!datasetId,
    refetchInterval: (data) => {
      const status = data?.dataset?.status;
      return status === 'geocoding' ? 3000 : false;
    }
  });

  const isGeocoding = datasetData?.dataset?.status === 'geocoding';
  const geocodingProgress = datasetData?.dataset?.geocoding_progress || 0;

  // Fetch voters for selected dataset
  const { data: votersData, isLoading: loadingVoters } = useQuery({
    queryKey: ['voters', datasetId, filters],
    queryFn: () => getVoters(datasetId, { filter: filters, limit: 10000 }),
    enabled: !!datasetId,
    refetchInterval: isGeocoding ? 5000 : false
  });

  // Fetch dataset stats
  const { data: statsData } = useQuery({
    queryKey: ['stats', datasetId],
    queryFn: () => getDatasetStats(datasetId),
    enabled: !!datasetId,
    refetchInterval: isGeocoding ? 5000 : false
  });

  const datasets = datasetsData?.datasets || [];
  const voters = votersData?.voters || [];
  const stats = statsData?.stats || {};

  // Get available fields for visualization
  const availableFields = useMemo(() => {
    if (voters.length === 0) return [];
    const firstVoter = voters[0];
    return Object.keys(firstVoter).filter(key =>
      !['id', 'lat', 'lng', 'latitude', 'longitude', 'address', 'dataset_id'].includes(key.toLowerCase())
    );
  }, [voters]);

  // Get sample values for fields
  const fieldSamples = useMemo(() => {
    if (voters.length === 0) return {};
    const samples = {};
    availableFields.forEach(field => {
      const values = [...new Set(voters.slice(0, 100).map(v => v[field]).filter(v => v != null))];
      samples[field] = values.slice(0, 5);
    });
    return samples;
  }, [voters, availableFields]);

  // Visualization mutation
  const visualizeMutation = useMutation({
    mutationFn: (prompt) => generateVisualization(datasetId, prompt, availableFields, fieldSamples),
    onSuccess: (data) => {
      const spec = data.visualization;
      setVisualizationSpec(spec);

      if (spec.color_scheme) {
        setColorScheme(spec.color_scheme);
      }
      if (spec.color_field) {
        setColorBy(spec.color_field);
      }

      setClusterConfig({
        colorField: spec.cluster_color_field || spec.color_field || null,
        colorScheme: spec.cluster_color_scheme || spec.color_scheme || null,
        showComposition: spec.cluster_show_composition !== false,
        sizeField: spec.cluster_size_field || null,
        highlightRatio: spec.cluster_highlight_ratio !== false
      });

      setHeatmapConfig({
        show: spec.show_heatmap_layer === true,
        field: spec.heatmap_field || null,
        color: spec.heatmap_color || '#3B82F6',
        radius: spec.heatmap_radius || 25,
        blur: spec.heatmap_blur || 15,
        opacity: spec.heatmap_opacity || 0.6
      });

      setBubbleConfig({
        show: spec.show_bubbles === true,
        sizeField: spec.bubble_size_field || null,
        minRadius: spec.bubble_min_radius || 8,
        maxRadius: spec.bubble_max_radius || 40,
        opacity: spec.bubble_opacity || 0.7
      });

      setHexbinConfig({
        show: spec.show_hexbin === true,
        radius: spec.hexbin_radius || 50,
        colorField: spec.hexbin_color_field || null,
        colorScheme: spec.hexbin_color_scheme || null,
        opacity: spec.hexbin_opacity || 0.7
      });

      if (spec.highlight_matching && data.matchingVoterIds) {
        setHighlightedVoterIds(data.matchingVoterIds);
      } else if (data.matchingVoterIds && data.matchingVoterIds.length > 0) {
        setHighlightedVoterIds(data.matchingVoterIds);
      } else {
        setHighlightedVoterIds([]);
      }
    }
  });

  // Route optimization mutation
  const routesMutation = useMutation({
    mutationFn: (params) => optimizeRoutes(datasetId, params),
    onSuccess: (data) => {
      setRoutes(data.routes || []);
      setShowRoutes(true);
    }
  });

  // Create route mutation (for starting canvassing)
  const createRouteMutation = useMutation({
    mutationFn: (routeData) => createRoute(datasetId, routeData),
    onSuccess: (data) => {
      // Navigate to the canvasser view with the new route ID
      if (data.route_id) {
        navigate(`/canvass/${data.route_id}`);
      }
    }
  });

  // Helper to get field value with case-insensitivity
  const getFieldValue = (voter, field) => {
    if (voter[field] !== undefined) return voter[field];
    const capitalized = field.charAt(0).toUpperCase() + field.slice(1);
    if (voter[capitalized] !== undefined) return voter[capitalized];
    if (voter[field.toUpperCase()] !== undefined) return voter[field.toUpperCase()];
    return undefined;
  };

  // Calculate stats for legend
  const voterStats = useMemo(() => {
    const statObj = {};
    voters.forEach(voter => {
      let key;
      switch (colorBy) {
        case 'party':
          key = getFieldValue(voter, 'party') || 'default';
          break;
        case 'sex':
          key = getFieldValue(voter, 'sex') || 'default';
          break;
        case 'age':
          const age = getFieldValue(voter, 'age');
          if (age >= 18 && age <= 25) key = '18-25';
          else if (age >= 26 && age <= 35) key = '26-35';
          else if (age >= 36 && age <= 50) key = '36-50';
          else if (age >= 51 && age <= 65) key = '51-65';
          else if (age > 65) key = '65+';
          else key = 'default';
          break;
        default:
          key = 'default';
      }
      statObj[key] = (statObj[key] || 0) + 1;
    });
    return statObj;
  }, [voters, colorBy]);

  // Handle color by change
  const handleColorByChange = (field) => {
    setColorBy(field);
    setColorScheme(defaultColorSchemes[field] || defaultColorSchemes.party);
    setLabels(defaultLabels[field] || defaultLabels.party);
  };

  // Handle scheme change (from legend configuration)
  const handleSchemeChange = (newScheme, newLabels) => {
    setColorScheme(newScheme);
    setLabels(newLabels);
  };

  // Handle filter change
  const handleFilterChange = (key, value) => {
    if (key === 'reset') {
      setFilters({});
    } else {
      setFilters(prev => ({
        ...prev,
        [key]: value
      }));
    }
  };

  // Handle dataset change
  const handleDatasetChange = (e) => {
    const newDatasetId = e.target.value;
    if (newDatasetId) {
      setSearchParams({ dataset: newDatasetId });
    } else {
      setSearchParams({});
    }
    setVisualizationSpec(null);
    setHighlightedVoterIds([]);
    setRoutes([]);
    setShowRoutes(false);
  };

  // Handle natural language visualization
  const handleVisualizationSubmit = useCallback(async (prompt) => {
    await visualizeMutation.mutateAsync(prompt);
  }, [visualizeMutation]);

  // Handle clear visualization
  const handleClearVisualization = useCallback(() => {
    setVisualizationSpec(null);
    setHighlightedVoterIds([]);
    setColorBy('party');
    setColorScheme(defaultColorSchemes.party);
    setLabels(defaultLabels.party);
    setClusterConfig({
      colorField: null,
      colorScheme: null,
      showComposition: true,
      sizeField: null,
      highlightRatio: true
    });
    setHeatmapConfig({
      show: false,
      field: null,
      color: '#3B82F6',
      radius: 25,
      blur: 15,
      opacity: 0.6
    });
    setBubbleConfig({
      show: false,
      sizeField: null,
      minRadius: 8,
      maxRadius: 40,
      opacity: 0.7
    });
    setHexbinConfig({
      show: false,
      radius: 50,
      colorField: null,
      colorScheme: null,
      opacity: 0.7
    });
  }, []);

  // Handle route optimization
  const handleRouteOptimization = useCallback(async (params) => {
    await routesMutation.mutateAsync(params);
  }, [routesMutation]);

  // Handle clear routes
  const handleClearRoutes = useCallback(() => {
    setRoutes([]);
    setShowRoutes(false);
    setSelectedRoute(null);
  }, []);

  // Handle start canvassing
  const handleStartCanvassing = useCallback(async (routeIndex) => {
    const selectedRouteData = routes[routeIndex];
    if (!selectedRouteData || !datasetId) return;

    // Create the route in the backend and navigate to canvasser view
    await createRouteMutation.mutateAsync({
      canvasser_id: selectedRouteData.canvasser_id || `canvasser_${routeIndex + 1}`,
      stops: selectedRouteData.voters || selectedRouteData.stops || [],
      polyline: selectedRouteData.polyline,
      total_distance_miles: selectedRouteData.total_distance_miles,
      estimated_time_minutes: selectedRouteData.estimated_time_minutes
    });
  }, [routes, datasetId, createRouteMutation]);

  // Available fields for color coding (for manual selection)
  const colorByFields = [
    { value: 'party', label: 'Party' },
    { value: 'sex', label: 'Sex' },
    { value: 'age', label: 'Age Range' }
  ];

  // Sidebar content component to avoid duplication
  const SidebarContent = () => (
    <>
      {/* Tab navigation */}
      <TabNavigation activeTab={activeTab} onTabChange={setActiveTab} />

      {activeTab === 'visualize' && (
        <>
          {/* Dynamic Visualization Legend */}
          <VisualizationLegend
            visualizationSpec={visualizationSpec}
            matchingCount={highlightedVoterIds.length}
            totalCount={voters.filter(v => v.lat && v.lng).length}
            onClear={handleClearVisualization}
          />

          {/* Manual Legend (shown when no visualization active) */}
          {!visualizationSpec && (
            <MapLegend
              colorBy={colorBy}
              colorScheme={colorScheme}
              labels={labels}
              voterStats={voterStats}
              onSchemeChange={handleSchemeChange}
              onColorByChange={handleColorByChange}
              availableFields={colorByFields}
            />
          )}

          {/* Filters */}
          {showFilters && (
            <FilterPanel
              filters={filters}
              onFilterChange={handleFilterChange}
              stats={stats}
            />
          )}
        </>
      )}

      {activeTab === 'routes' && datasetId && (
        <>
          {/* Canvassing Config Button */}
          <div className="flex justify-end mb-2">
            <button
              onClick={() => setShowCanvassingConfig(true)}
              className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg"
              title="Configure canvassing status options"
            >
              <Cog6ToothIcon className="h-4 w-4" />
              <span>Configure Status Options</span>
            </button>
          </div>

          {/* Route Optimizer */}
          <RouteOptimizer
            datasetId={datasetId}
            voterCount={voters.filter(v => v.lat && v.lng).length}
            onOptimize={handleRouteOptimization}
            isLoading={routesMutation.isPending}
            currentSpec={visualizationSpec}
          />

          {/* Route Visualization */}
          {routes.length > 0 && (
            <RouteVisualization
              routes={routes}
              selectedRoute={selectedRoute}
              onRouteSelect={setSelectedRoute}
              onClearRoutes={handleClearRoutes}
              onStartCanvassing={handleStartCanvassing}
              isStartingRoute={createRouteMutation.isPending}
            />
          )}
        </>
      )}

      {/* Quick stats */}
      {datasetId && (
        <div className="bg-white rounded-lg shadow p-4">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">Dataset Info</h3>
          <dl className="space-y-2 text-sm">
            <div className="flex justify-between">
              <dt className="text-gray-500">Total Voters</dt>
              <dd className="text-gray-900 font-medium">
                {votersData?.total?.toLocaleString() || voters.length.toLocaleString()}
              </dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-gray-500">Displayed</dt>
              <dd className="text-gray-900 font-medium">
                {voters.filter(v => v.lat && v.lng).length.toLocaleString()}
              </dd>
            </div>
            {highlightedVoterIds.length > 0 && (
              <div className="flex justify-between">
                <dt className="text-gray-500">Matching Filter</dt>
                <dd className="text-blue-600 font-medium">
                  {highlightedVoterIds.length.toLocaleString()}
                </dd>
              </div>
            )}
            <div className="flex justify-between">
              <dt className="text-gray-500">Geocoded</dt>
              <dd className="text-gray-900 font-medium">
                {voters.length > 0
                  ? `${Math.round((voters.filter(v => v.lat && v.lng).length / voters.length) * 100)}%`
                  : '0%'}
              </dd>
            </div>
            {isGeocoding && (
              <div className="mt-3 pt-3 border-t border-gray-100">
                <div className="flex items-center gap-2 text-blue-600">
                  <ArrowPathIcon className="h-4 w-4 animate-spin" />
                  <span className="text-sm font-medium">Geocoding in progress...</span>
                </div>
                <div className="mt-2">
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${Math.round(geocodingProgress * 100)}%` }}
                    />
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    {Math.round(geocodingProgress * 100)}% complete
                  </div>
                </div>
              </div>
            )}

            {/* Geocoding stats with outlier info */}
            {datasetData?.dataset?.geocoding_stats && (
              <div className="mt-3 pt-3 border-t border-gray-100">
                <div className="text-xs text-gray-600 space-y-1">
                  {datasetData.dataset.geocoding_stats.outliers_detected > 0 && (
                    <div className="flex justify-between">
                      <span>Outliers detected:</span>
                      <span className="font-medium">{datasetData.dataset.geocoding_stats.outliers_detected}</span>
                    </div>
                  )}
                  {datasetData.dataset.geocoding_stats.outliers_fixed > 0 && (
                    <div className="flex justify-between text-green-600">
                      <span>Outliers fixed:</span>
                      <span className="font-medium">{datasetData.dataset.geocoding_stats.outliers_fixed}</span>
                    </div>
                  )}
                  {datasetData.dataset.geocoding_stats.discarded > 0 && (
                    <div className="flex justify-between text-amber-600">
                      <span>Unresolvable:</span>
                      <span className="font-medium">{datasetData.dataset.geocoding_stats.discarded}</span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </dl>
        </div>
      )}

      {/* Failed Geocodes Panel */}
      <FailedGeocodesPanel
        failedGeocodes={datasetData?.dataset?.failed_geocodes || []}
        totalFailed={datasetData?.dataset?.failed_geocodes_count || datasetData?.dataset?.failed_geocodes?.length || 0}
      />
    </>
  );

  return (
    <div className="h-[calc(100vh-6rem)] md:h-[calc(100vh-10rem)] flex flex-col">
      {/* Header - Compact on mobile */}
      <div className="flex flex-col gap-2 mb-2 md:mb-4">
        {/* Top row - title and dataset selector */}
        <div className="flex items-center gap-2 md:gap-3">
          <h1 className="text-lg md:text-xl font-bold text-gray-900 hidden sm:block">Voter Map</h1>

          {/* Dataset selector */}
          <select
            value={datasetId || ''}
            onChange={handleDatasetChange}
            className="flex-1 md:flex-none md:w-64 px-2 md:px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">Select dataset...</option>
            {datasets.map(ds => (
              <option key={ds.id} value={ds.id}>
                {ds.name} ({ds.voterCount?.toLocaleString()})
              </option>
            ))}
          </select>

          {/* Mobile controls inline */}
          <div className="flex items-center gap-1 md:hidden">
            {/* Toggle clustering */}
            <button
              onClick={() => setShowClusters(!showClusters)}
              className={`p-2 rounded-lg border ${showClusters
                  ? 'bg-blue-50 border-blue-200 text-blue-600'
                  : 'border-gray-300 text-gray-600'
                }`}
            >
              {showClusters ? <EyeIcon className="h-5 w-5" /> : <EyeSlashIcon className="h-5 w-5" />}
            </button>

            {/* Mobile sidebar toggle */}
            <button
              onClick={() => setShowMobileSidebar(true)}
              className="p-2 rounded-lg border border-gray-300 text-gray-600"
            >
              <AdjustmentsHorizontalIcon className="h-5 w-5" />
            </button>
          </div>

          {/* Desktop controls */}
          <div className="hidden md:flex items-center gap-2">
            {/* Search */}
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search voters..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm w-48 lg:w-64 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Toggle clustering */}
            <button
              onClick={() => setShowClusters(!showClusters)}
              className={`p-2 rounded-lg border ${showClusters
                  ? 'bg-blue-50 border-blue-200 text-blue-600'
                  : 'border-gray-300 text-gray-600 hover:bg-gray-50'
                }`}
              title={showClusters ? 'Disable clustering' : 'Enable clustering'}
            >
              {showClusters ? <EyeIcon className="h-5 w-5" /> : <EyeSlashIcon className="h-5 w-5" />}
            </button>

            {/* Toggle routes */}
            {routes.length > 0 && (
              <button
                onClick={() => setShowRoutes(!showRoutes)}
                className={`p-2 rounded-lg border ${showRoutes
                    ? 'bg-green-50 border-green-200 text-green-600'
                    : 'border-gray-300 text-gray-600 hover:bg-gray-50'
                  }`}
                title={showRoutes ? 'Hide routes' : 'Show routes'}
              >
                <MapPinIcon className="h-5 w-5" />
              </button>
            )}

            {/* Toggle filters */}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg border ${showFilters
                  ? 'bg-blue-50 border-blue-200 text-blue-600'
                  : 'border-gray-300 text-gray-600 hover:bg-gray-50'
                }`}
            >
              <FunnelIcon className="h-4 w-4" />
              <span className="hidden lg:inline">Filters</span>
            </button>
          </div>
        </div>
      </div>

      {/* Natural Language Bar - Hidden on mobile, visible on tablet+ */}
      {datasetId && (
        <div className="hidden md:block mb-4">
          <NaturalLanguageBar
            onSubmit={handleVisualizationSubmit}
            isLoading={visualizeMutation.isPending}
            currentSpec={visualizationSpec}
            onClear={handleClearVisualization}
            disabled={!datasetId || loadingVoters}
          />
        </div>
      )}

      {/* Main content */}
      <div className="flex gap-4 flex-1 min-h-0">
        {/* Map - Full width on mobile */}
        <div className="flex-1 bg-white rounded-lg shadow overflow-hidden">
          {!datasetId ? (
            <div className="h-full flex items-center justify-center text-gray-500 p-4 text-center">
              Select a dataset to view the voter map
            </div>
          ) : loadingVoters ? (
            <div className="h-full flex items-center justify-center">
              <ArrowPathIcon className="h-8 w-8 text-blue-600 animate-spin" />
              <span className="ml-2 text-gray-600">Loading voters...</span>
            </div>
          ) : (
            <VoterMapComponent
              voters={voters}
              colorBy={colorBy}
              colorScheme={colorScheme}
              showClusters={showClusters}
              routes={showRoutes ? routes : []}
              showRoutes={showRoutes}
              highlightedVoterIds={highlightedVoterIds}
              dimNonHighlighted={highlightedVoterIds.length > 0}
              clusterConfig={clusterConfig}
              heatmapConfig={heatmapConfig}
              bubbleConfig={bubbleConfig}
              hexbinConfig={hexbinConfig}
              selectedRoute={selectedRoute}
              onRouteSelect={setSelectedRoute}
            />
          )}
        </div>

        {/* Desktop Sidebar - Hidden on mobile */}
        <div className="hidden md:block w-72 lg:w-80 space-y-4 overflow-y-auto">
          <SidebarContent />
        </div>
      </div>

      {/* Mobile Sidebar Overlay */}
      {showMobileSidebar && (
        <div className="fixed inset-0 z-50 md:hidden">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setShowMobileSidebar(false)}
          />

          {/* Slide-up panel */}
          <div className="absolute bottom-0 left-0 right-0 bg-white rounded-t-2xl max-h-[80vh] overflow-hidden flex flex-col animate-slide-up">
            {/* Handle bar */}
            <div className="flex items-center justify-center py-3 border-b border-gray-200">
              <div className="w-10 h-1 bg-gray-300 rounded-full" />
            </div>

            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Map Options</h2>
              <button
                onClick={() => setShowMobileSidebar(false)}
                className="p-2 text-gray-500 hover:text-gray-700"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              <SidebarContent />
            </div>

            {/* Close button at bottom */}
            <div className="p-4 border-t border-gray-200">
              <button
                onClick={() => setShowMobileSidebar(false)}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700"
              >
                <ChevronUpIcon className="h-5 w-5" />
                Close Options
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Mobile AI Visualization FAB - only visible on mobile when dataset selected */}
      {datasetId && !showMobileSidebar && !showMobilePromptBar && (
        <button
          onClick={() => setShowMobilePromptBar(true)}
          className="fixed bottom-20 right-4 md:hidden z-40 w-14 h-14 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-full shadow-lg flex items-center justify-center hover:from-purple-700 hover:to-blue-700 active:scale-95 transition-all"
          aria-label="AI Visualization"
        >
          <SparklesIcon className="h-6 w-6" />
        </button>
      )}

      {/* Mobile AI Visualization Bottom Sheet */}
      {showMobilePromptBar && (
        <div className="fixed inset-0 z-50 md:hidden">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setShowMobilePromptBar(false)}
          />

          {/* Slide-up panel */}
          <div className="absolute bottom-0 left-0 right-0 bg-white rounded-t-2xl overflow-hidden flex flex-col animate-slide-up">
            {/* Handle bar */}
            <div className="flex items-center justify-center py-3">
              <div className="w-10 h-1 bg-gray-300 rounded-full" />
            </div>

            {/* Header */}
            <div className="flex items-center justify-between px-4 pb-3 border-b border-gray-200">
              <div className="flex items-center gap-2">
                <SparklesIcon className="h-5 w-5 text-purple-600" />
                <h2 className="text-lg font-semibold text-gray-900">AI Visualization</h2>
              </div>
              <button
                onClick={() => setShowMobilePromptBar(false)}
                className="p-2 text-gray-500 hover:text-gray-700"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            {/* Natural Language Bar */}
            <div className="p-4">
              <NaturalLanguageBar
                onSubmit={async (prompt) => {
                  await handleVisualizationSubmit(prompt);
                  setShowMobilePromptBar(false);
                }}
                isLoading={visualizeMutation.isPending}
                currentSpec={visualizationSpec}
                onClear={() => {
                  handleClearVisualization();
                  setShowMobilePromptBar(false);
                }}
                disabled={!datasetId || loadingVoters}
              />
            </div>

            {/* Current visualization indicator */}
            {visualizationSpec && (
              <div className="px-4 pb-4">
                <div className="flex items-center gap-2 p-3 bg-purple-50 rounded-lg">
                  <SparklesIcon className="h-4 w-4 text-purple-600 flex-shrink-0" />
                  <span className="text-sm text-purple-700 truncate">
                    Active: {visualizationSpec.description || 'Custom visualization'}
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Canvassing Configuration Panel */}
      <CanvassingConfigPanel
        datasetId={datasetId}
        isOpen={showCanvassingConfig}
        onClose={() => setShowCanvassingConfig(false)}
      />
    </div>
  );
}
